import type { BalanceBundle, Frame } from '@guildrise/shared';
import { isValidDiscriminator, NAME_MAX_LENGTH, NAME_MIN_LENGTH } from '@guildrise/shared';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError, checkGuildName } from '../../shared/api.js';
import { useTelegramBack } from '../../shared/telegram-back.js';
import { Button } from '../../shared/ui/controls.js';
import { GuildAvatar } from '../../shared/ui/guild-avatar.js';
import { Icon } from '../../shared/ui/icons.js';
import { compressToWebp } from './compress-logo.js';

/*
 * Мастер создания гильдии: знакомство → ник → логотип → обводка.
 *
 * Компоновка каждого шага одинаковая:
 *   шапка (номер шага, заголовок, пояснение)
 *   сцена — главный объект шага по центру свободного места
 *   панель действия, закреплённая снизу с отступом safe-area
 *
 * Сцена занимает всё место между шапкой и панелью. Прежняя вёрстка
 * складывала всё под заголовок и оставляла половину экрана пустой.
 *
 * «Назад» — системная кнопка Telegram (стек.md), своя не рисуется.
 */

export type WizardStep = 'intro' | 'name' | 'logo' | 'frame';

const STEPS: readonly WizardStep[] = ['intro', 'name', 'logo', 'frame'];

interface Props {
  readonly balance: BalanceBundle;
  readonly playerName: string;
  readonly busy: boolean;
  readonly errorMessage: string | null;
  /**
   * Сервер ответил NAME_TAKEN на создании: пару заняли, пока игрок шёл
   * по мастеру. Мастер возвращается на шаг ника с подсказкой.
   */
  readonly takenSuggestion: string | null | undefined;
  readonly onSubmit: (input: GuildDraft) => void;
  readonly onUploadLogo: (blob: Blob) => Promise<string>;
}

export interface GuildDraft {
  readonly name: string;
  readonly discriminator: string;
  readonly logoKey: string | null;
  readonly frameId: string;
}

export function CreateGuildWizard({
  balance,
  playerName,
  busy,
  errorMessage,
  takenSuggestion,
  onSubmit,
  onUploadLogo,
}: Props): React.ReactElement {
  const [step, setStep] = useState<WizardStep>('intro');
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [frameId, setFrameId] = useState('frame_basic');
  const [uploaded, setUploaded] = useState<{ key: string; url: string } | null>(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const frames = useMemo(
    () => balance.frames.filter((f) => f.available_at_start),
    [balance.frames],
  );

  const frame = frames.find((f) => f.frame_id === frameId);
  const frameArt = frame?.kind === 'art' ? frame.art : undefined;

  const check = useNameCheck(name, tag, step === 'name');
  const index = STEPS.indexOf(step);

  // Пару «ник#номер» заняли, пока игрок выбирал логотип и обводку:
  // возвращаем на шаг ника, проверка там покажет «занято» и подсказку.
  useEffect(() => {
    if (takenSuggestion !== undefined) setStep('name');
  }, [takenSuggestion]);

  const goNext = useCallback(() => {
    const next = STEPS[index + 1];
    if (next !== undefined) setStep(next);
  }, [index]);

  const goBack = useCallback(() => {
    const prev = STEPS[index - 1];
    if (prev !== undefined) setStep(prev);
  }, [index]);

  // На первом экране «Назад» некуда — кнопка скрыта. Пока идёт создание,
  // уходить со страницы нельзя: ответ сервера потерялся бы.
  useTelegramBack(step === 'intro' || busy ? null : goBack);

  // Освобождаем память от прежней картинки предпросмотра.
  useEffect(() => {
    const url = uploaded?.url;
    return () => {
      if (url !== undefined) URL.revokeObjectURL(url);
    };
  }, [uploaded?.url]);

  const handleFile = useCallback(
    async (file: File) => {
      setUploadError(null);
      setUploadBusy(true);
      try {
        // Сжимаем до отправки: фото с телефона — 5 МБ, после обработки ~20 КБ.
        const blob = await compressToWebp(file);
        const key = await onUploadLogo(blob);
        setUploaded({ key, url: URL.createObjectURL(blob) });
      } catch {
        setUploadError('Картинку загрузить не удалось. Попробуй другую.');
      } finally {
        setUploadBusy(false);
      }
    },
    [onUploadLogo],
  );

  const logoUrl = uploaded?.url;

  /*
   * Фон и полоса прогресса живут вне ключа шага: при переходе React
   * пересоздаёт только содержимое шага. Раньше ключ стоял на всём <main>,
   * фон заново проявлялся с нуля и экран на 200 мс темнел — это и было
   * «дёрганье» между шагами.
   */
  return (
    <main className={`wizard${step === 'intro' ? ' wizard--intro' : ''}`}>
      <div className={`title-art${step === 'intro' ? '' : ' title-art--dim'}`} aria-hidden="true" />
      {step !== 'intro' && <Progress current={index} total={STEPS.length - 1} />}
      <div className="wizard__step" key={step}>
        {step === 'intro' && <IntroStep playerName={playerName} onNext={goNext} />}

        {step === 'name' && (
          <NameStep
            name={name}
            tag={tag}
            onName={setName}
            onTag={setTag}
            check={check}
            onNext={goNext}
          />
        )}

        {step === 'logo' && (
          <LogoStep
            name={name}
            frameId={frameId}
            frameArt={frameArt}
            logoUrl={logoUrl}
            busy={uploadBusy}
            error={uploadError}
            onFile={handleFile}
            onClear={() => setUploaded(null)}
            onNext={goNext}
          />
        )}

        {step === 'frame' && (
          <FrameStep
            name={name.trim()}
            tag={tag}
            logoUrl={logoUrl}
            frames={frames}
            frameId={frameId}
            onPick={setFrameId}
            busy={busy}
            errorMessage={errorMessage}
            onDone={() =>
              onSubmit({
                name: name.trim(),
                discriminator: tag,
                logoKey: uploaded?.key ?? null,
                frameId,
              })
            }
          />
        )}
      </div>
    </main>
  );
}

/* Полоса прогресса. Номер шага пишется в шапке — здесь только положение. */
function Progress({ current, total }: { readonly current: number; readonly total: number }) {
  return (
    <div
      className="progress"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={current}
      aria-label={`Шаг ${current} из ${total}`}
    >
      <div className="progress__fill" style={{ transform: `scaleX(${current / total})` }} />
    </div>
  );
}

function Header({
  eyebrow,
  title,
  text,
}: {
  readonly eyebrow: string;
  readonly title: string;
  readonly text: string;
}) {
  return (
    <header className="wizard__head enter">
      <p className="eyebrow">{eyebrow}</p>
      <h1 className="h1">{title}</h1>
      <p className="body">{text}</p>
    </header>
  );
}

/*
 * Шаг 0. Знакомство.
 *
 * Титульный экран: цветной город гильдии на фоне, капитан гильдии
 * сверху, сразу под ним — приветствие, заголовок и кнопка, в середине
 * экрана (решение 16). Карточки шагов и пояснение про ник убраны
 * по просьбе владельца — мастер сам покажет каждый шаг.
 *
 * Капитан есть только здесь: на шагах мастера он висел бы над полем
 * и гербом и спорил с ними за внимание.
 */
function IntroStep({
  playerName,
  onNext,
}: {
  readonly playerName: string;
  readonly onNext: () => void;
}) {
  return (
    <>
      <HeroArt />

      <section className="wizard__stage wizard__stage--intro">
        <div className="intro-panel">
          <p className="wordmark enter">GuildRise</p>
          <p className="eyebrow intro-hello enter enter--1">Здравствуй, {playerName}</p>
          <h1 className="h1 h1--display intro-title enter enter--1">Основай свою гильдию</h1>
        </div>
      </section>

      <footer className="wizard__bar">
        <Button wide className="btn--hero" onClick={onNext}>
          Основать гильдию
        </Button>
      </footer>
    </>
  );
}

/* Состояние проверки «ник#номер» на сервере. */
type NameCheck =
  | { status: 'idle' }
  | { status: 'checking' }
  | { status: 'free' }
  | { status: 'taken'; suggestion: string | null }
  | { status: 'invalid'; message: string };

/*
 * Проверка «ник#номер» с задержкой.
 *
 * Запрос на каждую букву засыпал бы сервер и упирался в rate-limit.
 * Поздний ответ отбрасывается по runId: иначе ответ на «Рас» пришёл бы
 * после «Рассвет» и показал чужой результат.
 */
function useNameCheck(name: string, tag: string, enabled: boolean): NameCheck {
  const [state, setState] = useState<NameCheck>({ status: 'idle' });
  const runIdRef = useRef(0);

  useEffect(() => {
    const trimmed = name.trim();
    const length = Array.from(trimmed).length;
    const runId = ++runIdRef.current;

    if (!enabled || length === 0) {
      setState({ status: 'idle' });
      return;
    }
    if (length < NAME_MIN_LENGTH) {
      setState({ status: 'invalid', message: `Ник не\xa0короче ${NAME_MIN_LENGTH}\xa0символов` });
      return;
    }
    if (length > NAME_MAX_LENGTH) {
      setState({ status: 'invalid', message: `Ник не\xa0длиннее ${NAME_MAX_LENGTH}\xa0символов` });
      return;
    }
    // Номер ещё не набран — ждём молча, это не ошибка.
    if (tag.length < 4) {
      setState({ status: 'idle' });
      return;
    }
    if (!isValidDiscriminator(tag)) {
      setState({ status: 'invalid', message: 'Номер от\xa00001 до\xa09999' });
      return;
    }

    setState({ status: 'checking' });
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const result = await checkGuildName(trimmed, tag);
          if (runId !== runIdRef.current) return;
          setState(
            result.available
              ? { status: 'free' }
              : { status: 'taken', suggestion: result.suggestion },
          );
        } catch (error) {
          if (runId !== runIdRef.current) return;
          const api = error instanceof ApiError ? error : null;
          setState({ status: 'invalid', message: api?.message ?? 'Проверить не\xa0удалось' });
        }
      })();
    }, 400);

    return () => clearTimeout(timer);
  }, [name, tag, enabled]);

  return state;
}

/*
 * Шаг 1. Ник.
 *
 * Поле — это и есть ник целиком: имя слева, «#» и четыре цифры справа.
 * Номер игрок вводит сам (решение 15); если пара занята, игра предлагает
 * свободный номер одной кнопкой. Компоновка — как у остальных шагов:
 * шапка, поле по центру, кнопка снизу (решение 16).
 */
function NameStep({
  name,
  tag,
  onName,
  onTag,
  check,
  onNext,
}: {
  readonly name: string;
  readonly tag: string;
  readonly onName: (v: string) => void;
  readonly onTag: (v: string) => void;
  readonly check: NameCheck;
  readonly onNext: () => void;
}) {
  const tagRef = useRef<HTMLInputElement>(null);
  const canNext = check.status === 'free';

  return (
    <>
      <Header
        eyebrow="Шаг 1 из 3"
        title="Твой ник"
        text="Название гильдии и номер из четырёх цифр. По номеру тебя отличат от тёзок."
      />

      <section className="wizard__stage">
        <div className="nick enter enter--1" data-state={check.status}>
          <label className="nick__name">
            <span className="sr-only">Название гильдии</span>
            <input
              id="guild-name"
              className="nick__field"
              value={name}
              maxLength={NAME_MAX_LENGTH * 2}
              placeholder="Рассвет"
              autoComplete="off"
              autoCapitalize="words"
              spellCheck={false}
              enterKeyHint="next"
              // biome-ignore lint/a11y/noAutofocus: единственное поле экрана, ввод — всё, что здесь делают
              autoFocus
              onChange={(e) => onName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') tagRef.current?.focus();
              }}
              aria-describedby="name-status"
            />
          </label>
          <label className="nick__tag">
            <span className="nick__hash" aria-hidden="true">
              #
            </span>
            <span className="sr-only">Номер, четыре цифры</span>
            <input
              ref={tagRef}
              className="nick__digits tabular"
              value={tag}
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              placeholder="0000"
              autoComplete="off"
              enterKeyHint="next"
              // Только цифры: буквы и пробелы отбрасываются при вводе.
              onChange={(e) => onTag(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canNext) onNext();
              }}
              aria-describedby="name-status"
            />
          </label>
        </div>

        <div id="name-status" className="nick-status enter enter--2" aria-live="polite">
          {check.status === 'idle' && (
            <span className="name-status">
              Название от {NAME_MIN_LENGTH} до {NAME_MAX_LENGTH} символов
            </span>
          )}
          {check.status === 'checking' && <span className="name-status">Проверяем…</span>}
          {check.status === 'free' && (
            <span className="name-status name-status--free">
              <Icon name="check" size={14} strokeWidth={2.5} /> Ник свободен
            </span>
          )}
          {check.status === 'invalid' && (
            <span className="name-status name-status--taken">{check.message}</span>
          )}
          {check.status === 'taken' && (
            <>
              <span className="name-status name-status--taken">Такой ник уже занят</span>
              {check.suggestion !== null && (
                <button
                  type="button"
                  className="link-btn nick-suggest"
                  onClick={() => onTag(check.suggestion ?? '')}
                >
                  Взять свободный #{check.suggestion}
                </button>
              )}
            </>
          )}
        </div>
      </section>

      <footer className="wizard__bar">
        <Button wide disabled={!canNext} onClick={onNext}>
          Далее
        </Button>
      </footer>
    </>
  );
}

/* Капитан гильдии сверху титульного экрана. */
function HeroArt() {
  // Персонаж проявляется, когда картинка готова: без «полупустого» кадра.
  const [ready, setReady] = useState(false);

  return (
    <img
      className={`hero-art${ready ? ' hero-art--ready' : ''}`}
      src="/art/hero-420.webp"
      srcSet="/art/hero-420.webp 420w, /art/hero-840.webp 840w"
      sizes="334px"
      width={1014}
      height={1399}
      alt=""
      aria-hidden="true"
      decoding="async"
      fetchPriority="high"
      onLoad={() => setReady(true)}
      ref={(img) => {
        // Из кеша картинка готова раньше, чем React подпишется на onLoad.
        if (img?.complete) setReady(true);
      }}
    />
  );
}

/* Шаг 2. Логотип. Нажатие на сам аватар открывает галерею. */
function LogoStep({
  name,
  frameId,
  frameArt,
  logoUrl,
  busy,
  error,
  onFile,
  onClear,
  onNext,
}: {
  readonly name: string;
  readonly frameId: string;
  readonly frameArt: string | undefined;
  readonly logoUrl: string | undefined;
  readonly busy: boolean;
  readonly error: string | null;
  readonly onFile: (file: File) => Promise<void>;
  readonly onClear: () => void;
  readonly onNext: () => void;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const hasLogo = logoUrl !== undefined;

  return (
    <>
      <Header
        eyebrow="Шаг 2 из 3"
        title="Логотип"
        text="Его видят на твоём профиле. Без картинки останется первая буква ника."
      />

      <section className="wizard__stage">
        <button
          type="button"
          className="logo-pick enter enter--1"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          aria-label={hasLogo ? 'Заменить картинку' : 'Выбрать картинку'}
        >
          <GuildAvatar
            frameId={frameId}
            frameArt={frameArt}
            name={name}
            logoUrl={logoUrl}
            size={184}
          />
          <span className="logo-pick__badge" aria-hidden="true">
            {busy ? <span className="spinner" /> : <Icon name="imagePlus" size={20} />}
          </span>
        </button>

        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void onFile(file);
          }}
        />

        {hasLogo && !busy && (
          <button type="button" className="link-btn link-btn--muted enter" onClick={onClear}>
            Убрать картинку
          </button>
        )}
        {error !== null && <p className="name-status name-status--taken">{error}</p>}
      </section>

      {/*
       * Главное действие шага — выбрать картинку. «Пропустить» вторично:
       * прежде пропуск был самой крупной кнопкой экрана, а выбор — ссылкой.
       */}
      <footer className="wizard__bar">
        {hasLogo ? (
          <Button wide disabled={busy} onClick={onNext}>
            Далее
          </Button>
        ) : (
          <>
            <Button wide disabled={busy} onClick={() => fileRef.current?.click()}>
              {busy ? 'Загружаем…' : 'Выбрать из галереи'}
            </Button>
            <button
              type="button"
              className="link-btn link-btn--muted"
              disabled={busy}
              onClick={onNext}
            >
              Пропустить
            </button>
          </>
        )}
      </footer>
    </>
  );
}

/*
 * Шаг 3. Обводка.
 *
 * Выбирают внешний вид — значит, и варианты показаны внешним видом:
 * живые кружки с этой обводкой. Подписи под кружками убраны: выбранную
 * называет строка под большим гербом, остальные узнаются по картинке.
 *
 * Сетка 4 в ряд прокручивается внутри сцены: 13 обводок не помещаются
 * на экран целиком, а прокрутка всего мастера сдвигала бы большой герб.
 */
function FrameStep({
  name,
  tag,
  logoUrl,
  frames,
  frameId,
  onPick,
  busy,
  errorMessage,
  onDone,
}: {
  readonly name: string;
  readonly tag: string;
  readonly logoUrl: string | undefined;
  readonly frames: readonly Frame[];
  readonly frameId: string;
  readonly onPick: (id: string) => void;
  readonly busy: boolean;
  readonly errorMessage: string | null;
  readonly onDone: () => void;
}) {
  const current = frames.find((f) => f.frame_id === frameId);
  const artOf = (f: Frame | undefined) => (f?.kind === 'art' ? f.art : undefined);

  return (
    <>
      <Header eyebrow="Шаг 3 из 3" title="Обводка" text="Выбери рамку вокруг герба гильдии." />

      <section className="wizard__stage wizard__stage--frames">
        <div className="frame-hero enter enter--1">
          <div className="frame-hero__avatar">
            <GuildAvatar
              frameId={frameId}
              frameArt={artOf(current)}
              name={name}
              logoUrl={logoUrl}
              size={132}
            />
          </div>
          <div className="frame-hero__caption">
            <span className="frame-hero__name">
              {name}
              <span className="frame-hero__tag tabular">#{tag}</span>
            </span>
            <span className="frame-hero__frame">{current?.label ?? ''}</span>
          </div>
        </div>

        {/* Нативные radio: стрелки на клавиатуре и озвучка скринридером без ARIA-ручной работы. */}
        <fieldset className="frame-grid enter enter--2" disabled={busy}>
          <legend className="sr-only">Обводка</legend>
          {frames.map((f) => {
            const selected = f.frame_id === frameId;
            return (
              <label key={f.frame_id} className={`frame-cell${selected ? ' frame-cell--on' : ''}`}>
                <input
                  className="sr-only"
                  type="radio"
                  name="frame"
                  value={f.frame_id}
                  checked={selected}
                  onChange={(e) => {
                    onPick(f.frame_id);
                    // Выбранная ячейка не должна оставаться под нижней тенью сетки.
                    e.currentTarget.parentElement?.scrollIntoView({ block: 'nearest' });
                  }}
                />
                <span className="sr-only">{f.label}</span>
                {/*
                 * Внешний размер у всех миниатюр один: металлическое кольцо
                 * занимает весь SVG, рисованная рамка вдвое шире своего SVG.
                 */}
                <GuildAvatar
                  frameId={f.frame_id}
                  frameArt={artOf(f)}
                  name={name}
                  logoUrl={logoUrl}
                  size={f.kind === 'art' ? 30 : 50}
                />
              </label>
            );
          })}
        </fieldset>
      </section>

      <footer className="wizard__bar">
        {errorMessage !== null && (
          <p className="name-status name-status--taken" role="alert">
            {errorMessage}
          </p>
        )}
        <Button wide disabled={busy} onClick={onDone}>
          {busy ? 'Основываем гильдию…' : 'Основать гильдию'}
        </Button>
      </footer>
    </>
  );
}
